import React from "react";
import { Badge } from "@/components/ui/badge";
import { Activity, Users, Eye, Target, MapPin } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  totalSessions: number;
  totalEvents: number;
  totalPageViews: number;
  uniqueVisitors: number;
  sessionsToday: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  realTimeVisitors: number;
  trafficSources: {
    utm: Record<string, number>;
  };
  locationStats: Array<{ location: string; count: number }>;
}

interface OverviewMetricsWidgetProps {
  stats: AnalyticsStats | null;
}

export const OverviewMetricsWidget: React.FC<OverviewMetricsWidgetProps> = ({
  stats,
}) => {
  const totalUTMVisitors = Object.values(
    stats?.trafficSources.utm || {},
  ).reduce((a, b) => a + b, 0);

  return (
    <DashboardWidget id="overview-metrics" title="Overview Metrics">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Real-time</span>
            <Activity className="h-4 w-4 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-600">
            {stats?.realTimeVisitors || 0}
          </div>
          <p className="text-xs text-muted-foreground">Currently online</p>
        </div>

        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Total Sessions</span>
            <Users className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{stats?.totalSessions || 0}</div>
          <p className="text-xs text-muted-foreground">
            +{stats?.sessionsToday || 0} today
          </p>
        </div>

        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">UTM Visitors</span>
            <Target className="h-4 w-4 text-primary" />
          </div>
          <div className="text-2xl font-bold text-primary">
            {totalUTMVisitors}
          </div>
          <p className="text-xs text-muted-foreground">Tracked sources</p>
        </div>

        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Page Views</span>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">{stats?.totalPageViews || 0}</div>
          <p className="text-xs text-muted-foreground">All time</p>
        </div>

        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Locations</span>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold">
            {stats?.locationStats?.length || 0}
          </div>
          <p className="text-xs text-muted-foreground">Unique cities</p>
        </div>
      </div>
    </DashboardWidget>
  );
};

export default OverviewMetricsWidget;
